import { stringParser } from '@/helpers/string-parser';
import router from '@/router';
import {
  getAction,
  getActionCampaigns,
  getActions,
  getActionTemplates,
  putAction,
  putAutomaticAction,
  updateAction,
  updateAutomaticAction,
} from '@/services/actions';
import { cloneDeep, escapeRegExp } from 'lodash';

import bus from '@/helpers/events/bus';

const defaultSuggestionDetails = {
  id: '',
  name: '',
  description: '',
  dayOfWeek: ['1', '2', '3', '4', '5', '6', '7'],
  dayOfYear: [],
  startDate: '',
  endDate: '',
  campaigns: [],
  enabledApproval: 0,
  isEnabledApproval: false,
  stores: '',
  storesId: [],
  storeSeller: '',
  actionCategory: null,
  enabled: false,
};

const suggestions = {
  namespaced: true,
  state: {
    activePage: 'composition',
    actionType: 'seller',
    actionName: '',
    actionsMessages: [],
    automaticAction: {
      startHour: '09:00',
      endHour: '18:00',
      limitType: 'nonofficial-automatic',
      sellerAllocation: null,
      audienceId: '',
      limitValue: null,
      sendChannel: 1,
      campaignId: null,
    },
    mode: null,
    suggestionsData: [],
    suggestionsList: [],
    suggestionDetail: cloneDeep(defaultSuggestionDetails),
    showToastSuccess: false,
    term: '',
    actionCategoryList: [],
    tooltipEmptyError: {
      actionCategory: false,
      stores: false,
      name: false,
      audience: false,
      limit: false,
      sellerAllocation: false,
      messages: false,
    },
    filterStatus: '',
    loadingSuggestions: false,
    loadingSuggestionsDetail: false,
    loadingActionTemplates: false,
    loadingCampaigns: false,
    disableCampaignsLoading: false,
    limitsSelectOptions: {
      no_limit: 'campaigns.limits.no_limit',
      seller: 'campaigns.limits.seller',
      store: 'campaigns.limits.store',
      brand: 'campaigns.limits.brand',
      store_seller: 'campaigns.limits.store_seller',
    },
    tabSelected: 'active',
    // pagination data
    id: 'actions-attachments',
    pagination: {
      count: 0,
      limit: 10,
      page: 1,
      order: '',
    },
    limitRange: [5, 10, 20, 30, 50],
    isDataUnchanged: true,
    isConfirmEditPriorityModalOpen: false,
    disabledButton: false,
    invalidHour: false,
    actionTemplates: [],
    hasSpecialCharactersName: false,
  },
  mutations: {
    SET_SELECTED_CATEGORY(state, payload) {
      state.suggestionDetail.actionCategory = payload;
    },
    SET_ACTIVE_PAGE(state, payload) {
      state.activePage = payload;
    },
    SET_PRIORITY_DATA_MODIFIED(state, payload) {
      state.isDataUnchanged = payload;
    },
    SET_OPEN_CONFIRM_EDIT_PRIORITY_MODAL(state, payload) {
      state.isConfirmEditPriorityModalOpen = payload;
    },
    SET_MODE(state, payload) {
      state.mode = payload;
    },
    SET_ACTION_TYPE(state, payload) {
      state.actionType = payload;
    },
    SET_ACTION_NAME(state, payload) {
      state.actionName = payload;
    },
    SET_AUTOMATIC_ACTION(state, payload) {
      state.automaticAction[payload.name] = payload.value;
    },
    SET_STATUS_TOOLTIP(state, payload) {
      state.tooltipEmptyError[payload.key] = payload.value;
    },
    SET_ACTION_CATEGORY_LIST(state, payload) {
      state.actionCategoryList = payload;
    },
    SET_SUGGESTIONS(state, data) {
      state.suggestionsList = data;
    },
    SET_ID(state, payload) {
      state.suggestionDetail.id = payload;
    },
    SET_SUGGESTION_NAME(state, payload) {
      state.suggestionDetail.name = payload;
    },
    SET_SUGGESTION_DESCRIPTION(state, payload) {
      state.suggestionDetail.description = payload;
    },
    SET_SUGGESTION_ENABLED_APPROVAL(state) {
      state.suggestionDetail.isEnabledApproval = !state.suggestionDetail.isEnabledApproval;
      state.suggestionDetail.enabledApproval = state.suggestionDetail.isEnabledApproval ? 1 : 0;
    },
    SET_SUGGESTION_IS_ENABLED_APPROVAL(state, payload) {
      state.suggestionDetail.isEnabledApproval = payload;
      state.suggestionDetail.enabledApproval = state.suggestionDetail.isEnabledApproval ? 1 : 0;
    },
    SET_SUGGESTION_STORES(state, payload) {
      state.suggestionDetail.stores = payload ?? [];
    },
    SET_SUGGESTION_STORES_ID(state, payload) {
      state.suggestionDetail.storesId = payload;
    },
    SET_SUGGESTION_DAYOFWEEK(state, payload) {
      state.suggestionDetail.dayOfWeek = payload;
    },
    SET_SUGGESTION_DAYOFYEAR(state, payload) {
      state.suggestionDetail.dayOfYear = payload;
    },
    SET_SUGGESTION_STARTDATE(state, payload) {
      state.suggestionDetail.startDate = payload;
    },
    SET_SUGGESTION_ENDDATE(state, payload) {
      state.suggestionDetail.endDate = payload;
    },
    SET_ACTION_STARTEND_DATES(state, payload) {
      state.suggestionDetail.startDate = payload.startDate || '';
      state.suggestionDetail.endDate = payload.endDate || '';
    },
    SET_SUGGESTION_CAMPAIGNS(state, payload) {
      state.suggestionDetail.campaigns = payload;
    },

    SET_SUGGESTION_ACTION_TEMPLATES(state, payload) {
      state.actionTemplates = payload;
    },
    SET_SUGGESTION_DETAILS(state, suggestionDetails) {
      if (state.mode === 'create') return;
      const {
        id,
        name,
        description,
        enabledApproval,
        stores,
        dayOfWeek,
        dayOfYear,
        startDate,
        endDate,
        actionCategory,
        enabled,
      } = suggestionDetails;

      state.suggestionDetail.id = id;
      state.suggestionDetail.name = name;
      state.suggestionDetail.description = description;
      state.suggestionDetail.enabledApproval = Number(enabledApproval);
      state.suggestionDetail.stores = stores;
      state.suggestionDetail.dayOfWeek = dayOfWeek;
      state.suggestionDetail.dayOfYear = dayOfYear;
      state.suggestionDetail.startDate = startDate;
      state.suggestionDetail.endDate = endDate;
      state.suggestionDetail.actionCategory = actionCategory;
      state.suggestionDetail.enabled = enabled;
    },
    SET_SHOW_TOAST_SUCCESS(state, value) {
      state.showToastSuccess = value;
    },
    SET_SEARCH_TERM(state, term) {
      state.term = term;
    },

    SET_FILTER_STATUS(state, value) {
      state.filterStatus = value;
    },

    SET_LOADING_SUGGESTIONS(state, payload) {
      state.loadingSuggestions = payload;
    },

    SET_LOADING_SUGGESTIONS_DETAIL(state, payload) {
      state.loadingSuggestionsDetail = payload;
    },

    SET_LOADING_ACTION_TEMPLATES(state, payload) {
      state.loadingActionTemplates = payload;
    },

    SET_LOADING_CAMPAIGNS(state, payload) {
      state.loadingCampaigns = payload;
    },

    SET_SAVE_AUDIENCE(state, payload) {
      state.automaticAction.audienceId = payload.id;
      state.suggestionDetail.campaigns[0].audience = payload;
    },

    SET_AUDIENCE_CAMPAIGNS(state, payload) {
      if (!state.suggestionDetail.campaigns) state.suggestionDetail.campaigns = [];
      state.suggestionDetail.campaigns.push(payload);
    },

    SET_EDIT_AUDIENCE_CAMPAIGNS(
      state,
      { name, audience, index, limitType, limitData, limitGroupRemaining, sellerAllocation },
    ) {
      state.suggestionDetail.campaigns[index].name = name ? name.trim() : '';
      state.suggestionDetail.campaigns[index].audience = audience;
      state.suggestionDetail.campaigns[index].limitType = limitType;
      state.suggestionDetail.campaigns[index].limitData = limitData;
      state.suggestionDetail.campaigns[index].limitGroupRemaining = limitGroupRemaining;
      state.suggestionDetail.campaigns[index].sellerAllocation = sellerAllocation;
    },

    SET_DELETE_ACTION(state, index) {
      const campaignDisabled = state.suggestionDetail.campaigns[index];
      campaignDisabled.enabled = 2;
      campaignDisabled.status = 2;
      state.suggestionDetail.campaigns.splice(index, 1, campaignDisabled);
    },

    SET_ACTIVE_CAMPAIGN(state, index) {
      const campaignDisabled = state.suggestionDetail.campaigns[index];
      campaignDisabled.enabled = 1;
      campaignDisabled.status = 1;
      state.suggestionDetail.campaigns.splice(index, 1, campaignDisabled);
    },

    SET_INACTIVE_CAMPAIGN(state, index) {
      const campaignDisabled = state.suggestionDetail.campaigns[index];
      campaignDisabled.enabled = 0;
      campaignDisabled.status = 0;
      state.suggestionDetail.campaigns.splice(index, 1, campaignDisabled);
    },

    SET_EXPORT_TOTALCOUNT(state, total) {
      state.pagination.count = total;
    },

    SET_SUGGESTION_QUARANTINE(state, payload) {
      state.suggestionDetail.quarantine = payload;
    },

    SET_ACTIONS_MESSAGES(state, payload) {
      state.actionsMessages.push(payload);
    },

    SET_EDIT_ACTIONS_MESSAGES(state, payload) {
      state.actionsMessages = payload;
    },

    RESET(state) {
      state.suggestionDetail = cloneDeep(defaultSuggestionDetails);
    },

    RESET_AUTOMATIC_ACTION(state) {
      state.automaticAction = {
        startHour: '09:00',
        endHour: '18:00',
        limitType: 'nonofficial-automatic',
        sellerAllocation: null,
        audienceId: '',
        limitValue: null,
        sendChannel: 1,
      };
    },

    SET_TAB(state, payload) {
      state.tabSelected = payload;
    },

    SET_DISABLED_BUTTON(state, payload) {
      state.disabledButton = payload;
    },

    SET_INVALID_HOUR(state, payload) {
      state.invalidHour = payload;
    },

    RESET_STATUS(state) {
      state.filterStatus = '';
    },

    DISABLE_CAMPAIGNS_LOADING(state, payload) {
      state.disableCampaignsLoading = payload;
    },

    SET_ERROR_NAME(state, value) {
      state.hasSpecialCharactersName = value;
    },
  },
  actions: {
    setActivePage({ commit }, payload) {
      commit('SET_ACTIVE_PAGE', payload);
    },

    setPriorityDataModified({ commit }, payload) {
      commit('SET_PRIORITY_DATA_MODIFIED', payload);
    },

    setOpenConfirmEditPriorityModal({ commit }, payload) {
      commit('SET_OPEN_CONFIRM_EDIT_PRIORITY_MODAL', payload);
    },

    setMode({ commit }, payload) {
      commit('SET_MODE', payload);

      if (payload === 'create') {
        commit('SET_SUGGESTION_ACTION_TEMPLATES', []);
      }
    },

    setActionType({ commit }, payload) {
      commit('SET_ACTION_TYPE', payload);
    },

    setActionName({ commit }, payload) {
      commit('SET_ACTION_NAME', payload);
    },

    setAutomaticAction({ commit }, payload) {
      commit('SET_AUTOMATIC_ACTION', payload);
    },

    setActionsMessages({ commit }, payload) {
      commit('SET_ACTIONS_MESSAGES', payload);
    },

    setEditActionsMessages({ commit }, payload) {
      commit('SET_EDIT_ACTIONS_MESSAGES', payload);
    },

    setStatusTooltip({ commit }, payload) {
      commit('SET_STATUS_TOOLTIP', payload);
    },

    setActionCategoryList({ commit }, payload) {
      commit('SET_ACTION_CATEGORY_LIST', payload);
    },

    // list data
    getActions({ commit, state }) {
      commit('SET_LOADING_SUGGESTIONS', true);
      commit('SET_SUGGESTIONS', []);
      getActions({
        page: state.pagination.page,
        limit: state.pagination.limit,
      })
        .then(({ data }) => {
          commit('SET_SUGGESTIONS', data?.data);
          commit('SET_EXPORT_TOTALCOUNT', data?.totalCount);
        })
        .catch(() => {
          commit('SET_LOADING_SUGGESTIONS', false);
        })
        .finally(() => {
          commit('SET_LOADING_SUGGESTIONS', false);
        });
    },

    getActionTemplates({ commit }, id) {
      commit('SET_LOADING_ACTION_TEMPLATES', true);

      getActionTemplates(id)
        .then(({ data }) => {
          commit('SET_SUGGESTION_ACTION_TEMPLATES', data?.data);
          commit('SET_EDIT_ACTIONS_MESSAGES', data?.data);
        })
        .catch(() => {
          commit('SET_LOADING_ACTION_TEMPLATES', false);
        })
        .finally(() => {
          commit('SET_LOADING_ACTION_TEMPLATES', false);
        });
    },
    // campaigns
    getActionCampaigns({ state, commit }, id) {
      const loadingStatus = state.disableCampaignsLoading ? false : true;

      commit('SET_LOADING_CAMPAIGNS', loadingStatus);

      getActionCampaigns(id)
        .then(({ data }) => {
          const automaticAction = {
            audienceId: data.data[0]?.audience?.id,
            limitData: data.data[0]?.limitData,
            limitType: data.data[0]?.limitType,
            sellerAllocation: data.data[0]?.sellerAllocation,
            campaignId: data.data[0]?.id,
          };

          commit('SET_AUTOMATIC_ACTION', { name: 'audienceId', value: automaticAction.audienceId });
          commit('SET_AUTOMATIC_ACTION', { name: 'sellerAllocation', value: automaticAction.sellerAllocation });
          commit('SET_AUTOMATIC_ACTION', { name: 'limitType', value: automaticAction.limitType });
          commit('SET_AUTOMATIC_ACTION', { name: 'limitValue', value: automaticAction.limitData?.limitValue });
          commit('SET_AUTOMATIC_ACTION', { name: 'campaignId', value: automaticAction.campaignId });

          commit('SET_SUGGESTION_CAMPAIGNS', data?.data);
        })
        .catch(() => {
          commit('SET_LOADING_CAMPAIGNS', false);
        })
        .finally(() => {
          commit('SET_LOADING_CAMPAIGNS', false);
          commit('DISABLE_CAMPAIGNS_LOADING', false);
        });
    },

    // detail data
    getSuggestionCampaign({ state, commit, dispatch }, id) {
      commit('SET_LOADING_SUGGESTIONS_DETAIL', true);

      getAction(id)
        .then(({ data }) => {
          const action = data.data;

          commit('SET_AUTOMATIC_ACTION', { name: 'startHour', value: action.startHour });
          commit('SET_AUTOMATIC_ACTION', { name: 'endHour', value: action.endHour });
          commit('SET_AUTOMATIC_ACTION', { name: 'sendChannel', value: action.sendChannel });
          commit('SET_ACTION_NAME', action.name);
          commit('SET_SUGGESTION_DETAILS', action);
          commit('SET_SUGGESTION_IS_ENABLED_APPROVAL', action.enabledApproval);
          commit('SET_SUGGESTION_STORES_ID', action.stores);
          commit('SET_SUGGESTION_STORES', action.stores);

          if (state.mode === 'edit' && !state.suggestionDetail.actionCategory && state.actionCategoryList.length) {
            dispatch('setStatusTooltip', {
              key: 'actionCategory',
              value: true,
            });
          }
        })
        .catch(() => {
          commit('SET_LOADING_SUGGESTIONS_DETAIL', false);
        })
        .finally(() => {
          commit('SET_LOADING_SUGGESTIONS_DETAIL', false);
        });
    },

    showToastSuccess({ commit }) {
      commit('SET_SHOW_TOAST_SUCCESS', true);
    },

    hideToastSuccess({ commit }) {
      commit('SET_SHOW_TOAST_SUCCESS', false);
    },
    setEnabledApproval({ commit }) {
      commit('SET_SUGGESTION_ENABLED_APPROVAL');
    },

    updateSuggestionStores({ commit }, payload) {
      commit('SET_SUGGESTION_STORES', payload);
    },

    updateSearchTerm({ commit }, term) {
      commit('SET_LOADING_SUGGESTIONS', true);
      commit('SET_SUGGESTIONS', []);
      getActions({ term })
        .then(({ data }) => {
          commit('SET_SUGGESTIONS', data?.data);
          commit('SET_EXPORT_TOTALCOUNT', data?.totalCount);
        })
        .catch(() => {
          commit('SET_LOADING_SUGGESTIONS', false);
        })
        .finally(() => {
          commit('SET_LOADING_SUGGESTIONS', false);
        });
    },

    updateListApprovalTerm({ commit }, term) {
      commit('SET_SEARCH_TERM', term);
      bus.$emit('updateApprovalTerm');
    },

    updateFilterStatus({ commit }, value) {
      commit('SET_FILTER_STATUS', value);
      bus.$emit('updateFilterStatus');
    },

    resetStatus({ commit }) {
      commit('RESET_STATUS');
    },

    setActionId({ commit, dispatch }, id) {
      commit('SET_ID', id);
      dispatch('getSuggestionCampaign', id);
      dispatch('getActionCampaigns', id);
      dispatch('getActionTemplates', id);
    },

    updateActionCampaigns({ commit, dispatch }, id) {
      commit('DISABLE_CAMPAIGNS_LOADING', true);

      dispatch('getActionCampaigns', id);
    },

    updateSuggestionName({ commit }, payload) {
      commit('SET_SUGGESTION_NAME', payload);
    },

    updateSuggestionDescription({ commit }, payload) {
      commit('SET_SUGGESTION_DESCRIPTION', payload);
    },

    updateDates({ commit }, payload) {
      commit('SET_ACTION_STARTEND_DATES', payload);
    },

    updateCampaigns({ commit }, payload) {
      commit('SET_SUGGESTION_CAMPAIGNS', payload);
    },

    updateQuarantine({ commit }, payload) {
      commit('SET_SUGGESTION_QUARANTINE', payload);
    },

    resetData({ commit }) {
      commit('RESET');
    },

    resetAutomaticAction({ commit }) {
      commit('RESET_AUTOMATIC_ACTION');
      commit('SET_EDIT_ACTIONS_MESSAGES', []);
    },

    actionAutomaticSubmit({ state, rootState, getters, commit, dispatch }) {
      commit('SET_DISABLED_BUTTON', true);

      const { id } = getters.getSuggestionDetail;
      const isEdit = getters.getMode === 'edit';

      const data = {
        name: state.actionName,
        startHour: state.automaticAction.startHour,
        endHour: state.automaticAction.endHour,
        messages: state.actionsMessages.map(message => ({
          text: message.text,
          lang: rootState.auth.user.language,
          ...(message.id && isEdit ? { id: message.id } : {}),
        })),
        campaigns: [
          {
            name: state.actionName,
            audienceId: state.automaticAction.audienceId,
            enabled: 1,
            sellerAllocation: state.automaticAction.sellerAllocation,
            limitType: state.automaticAction.limitType,
            limitData: { limitValue: state.automaticAction.limitValue },
          },
        ],
        sendChannel: 2,
        startDate: state.suggestionDetail.startDate,
        endDate: state.suggestionDetail.endDate,
        dayOfWeek: state.suggestionDetail.dayOfWeek,
        dayOfYear: state.suggestionDetail.dayOfYear,
        actionCategory: state.suggestionDetail.actionCategory,
      };

      if (!isEdit) {
        putAutomaticAction(data)
          .then(({ data }) => {
            if (!data.success) throw Error;

            if (data.success) {
              commit('SET_SHOW_TOAST_SUCCESS', true);
              commit('SET_DISABLED_BUTTON', false);

              router.push({
                name: 'campaign-campaigns',
                query: { edit: 'priority' },
              });
            }
          })
          .catch(() => {
            bus.$emit('open-suggestion-campaign-modal-error');
          })
          .finally(() => {
            dispatch('hideToastSuccess');
            commit('SET_DISABLED_BUTTON', false);
            commit('RESET_AUTOMATIC_ACTION');
          });
      }

      if (isEdit) {
        const updateData = {
          ...data,
          messages: state.actionsMessages.map(message => ({
            text: message.text,
            lang: rootState.auth.user.language,
            ...(message.id && !message.id.includes('msg-') ? { id: message.id } : {}),
          })),
          actionId: id,
          campaigns: [{ campaignId: state.automaticAction.campaignId, ...data.campaigns[0] }],
        };

        updateAutomaticAction(updateData)
          .then(({ data }) => {
            if (!data.success) throw Error;
            dispatch('getSuggestionCampaign', data?.data?.id);

            if (data.success) {
              commit('SET_SHOW_TOAST_SUCCESS', true);
              commit('SET_DISABLED_BUTTON', false);

              if (data.data.enabled) {
                setTimeout(() => {
                  router.push({
                    name: 'campaign-campaigns',
                    query: { edit: 'priority' },
                  });
                }, 2000);

                commit('SET_TAB', 'active');
              } else {
                setTimeout(() => {
                  router.push({
                    name: 'campaign-campaigns',
                  });
                }, 2000);

                commit('SET_TAB', 'deactive');
              }
            }
          })
          .catch(() => {
            bus.$emit('open-suggestion-campaign-modal-error');
          })
          .finally(() => {
            dispatch('hideToastSuccess');
            dispatch('setStatusTooltip', { key: 'name', value: false });
            dispatch('setStatusTooltip', { key: 'stores', value: false });
            dispatch('setStatusTooltip', { key: 'actionCategory', value: false });
            commit('SET_DISABLED_BUTTON', false);
          });
      }
    },

    actionSubmit({ commit, getters, dispatch }) {
      commit('SET_DISABLED_BUTTON', true);

      const {
        id,
        name,
        description,
        enabledApproval,
        stores,
        dayOfWeek,
        dayOfYear,
        startDate,
        endDate,
        campaigns,
        actionCategory,
      } = getters.getSuggestionDetail;

      const isEdit = getters.getMode === 'edit';

      const campaignsFormatted = campaigns
        ? campaigns.map(el => {
            return {
              campaignId: el?.id,
              name: el?.name.includes('(Inativa)') ? el?.name.replace('(Inativa)', '').trim() : el?.name,
              audienceId: el?.audience?.id,
              enabled: el?.enabled,
              sellerAllocation: el?.sellerAllocation,
              limitType: el?.limitType,
              limitGroupRemaining: el?.limitGroupRemaining,
              limitData:
                el?.limitData?.length > 0
                  ? el?.limitData?.map(item => {
                      return { ...item, stores: item?.stores?.comparisonStores?.join() };
                    })
                  : el?.limitData,
            };
          })
        : null;

      if (!isEdit) {
        putAction({
          name,
          description,
          enabledApproval,
          stores,
          startDate,
          endDate,
          dayOfWeek,
          dayOfYear,
          campaigns: JSON.stringify(campaignsFormatted),
          actionCategory,
        })
          .then(({ data }) => {
            if (!data.success) throw Error;

            if (data.success) {
              commit('SET_SHOW_TOAST_SUCCESS', true);
              commit('SET_DISABLED_BUTTON', false);

              router.push({
                name: 'campaign-campaigns',
                query: { edit: 'priority' },
              });
            }
          })
          .catch(() => {
            bus.$emit('open-suggestion-campaign-modal-error');
            commit('RESET');
          })
          .finally(() => {
            dispatch('hideToastSuccess');
            dispatch('setStatusTooltip', { key: 'name', value: false });
            dispatch('setStatusTooltip', { key: 'actionCategory', value: false });
            commit('SET_DISABLED_BUTTON', false);
          });
      }

      if (isEdit) {
        updateAction({
          actionId: id,
          name,
          description,
          enabledApproval,
          stores,
          startDate,
          endDate,
          dayOfWeek,
          dayOfYear,
          campaigns: JSON.stringify(campaignsFormatted),
          actionCategory,
        })
          .then(({ data }) => {
            if (!data.success) throw Error;
            dispatch('getSuggestionCampaign', data?.data?.id);

            if (data.success) {
              commit('SET_SHOW_TOAST_SUCCESS', true);
              commit('SET_DISABLED_BUTTON', false);

              if (data.data.enabled) {
                setTimeout(() => {
                  router.push({
                    name: 'campaign-campaigns',
                    query: { edit: 'priority' },
                  });
                }, 2000);

                commit('SET_TAB', 'active');
              } else {
                setTimeout(() => {
                  router.push({
                    name: 'campaign-campaigns',
                  });
                }, 2000);

                commit('SET_TAB', 'deactive');
              }
            }
          })
          .catch(() => {
            bus.$emit('open-suggestion-campaign-modal-error');
            commit('RESET');
          })
          .finally(() => {
            dispatch('hideToastSuccess');
            dispatch('setStatusTooltip', { key: 'name', value: false });
            dispatch('setStatusTooltip', { key: 'stores', value: false });
            dispatch('setStatusTooltip', { key: 'actionCategory', value: false });
            commit('SET_DISABLED_BUTTON', false);
          });
      }
    },

    actionSaveAudienceCampaigns({ commit }, payload) {
      commit('SET_AUDIENCE_CAMPAIGNS', payload);
    },

    actionSaveAudience({ commit }, payload) {
      commit('SET_SAVE_AUDIENCE', payload);
    },

    actionEditAudienceCampaigns({ commit }, payload) {
      commit('SET_EDIT_AUDIENCE_CAMPAIGNS', payload);
    },

    actionDeleteCampaign({ commit }, index) {
      commit('SET_DELETE_ACTION', index);
    },

    actionActiveCampaign({ commit }, index) {
      commit('SET_ACTIVE_CAMPAIGN', index);
    },

    actionInactiveCampaign({ commit }, index) {
      commit('SET_INACTIVE_CAMPAIGN', index);
    },

    setErrorName({ commit }, value) {
      commit('SET_ERROR_NAME', value);
    },

    setInvalidHour({ commit }, value) {
      commit('SET_INVALID_HOUR', value);
    },
  },
  getters: {
    getActivePage: state => state.activePage,
    getMode: state => state.mode,
    getActionsList: state => state.suggestionsList,
    getSuggestionDetail: state => state.suggestionDetail,
    getLoadingStatus: state => state.loadingSuggestions,
    getLoadingAction: state => state.loadingSuggestionsDetail,
    getLoadingCampaigns: state => state.loadingCampaigns,
    getLoadingActionTemplates: state => state.loadingActionTemplates,
    getIsEmptyCategoryTooltipVisible: state => state.tooltipEmptyError.actionCategory,
    getIsEmptyNameTooltipVisible: state => state.tooltipEmptyError.name,
    getFilteredSuggestionList: state => () => {
      if (!state.term) return state.suggestionsList;
      // applyParse => add scape regex characters and ignore accents from characters on term argument
      const applyParse = escapeRegExp(stringParser(state.term));
      return state.suggestionsList.filter(e => new RegExp(applyParse, 'gi').test(stringParser(e.action)));
    },
    getStoresId: state => state.suggestionDetail.storesId?.map(store => store.id),
    getSearchTermListApproval: state => state.term,
    getLimitsSelectOptions: state => state.limitsSelectOptions,
    hasEmptyRequiredFieldsAutomaticAction: state => {
      const { suggestionDetail, automaticAction, actionsMessages } = state;

      const isNameEmpty = !suggestionDetail?.name;
      const isCategoryEmpty = !suggestionDetail?.actionCategory;
      const isAudienceEmpty = !automaticAction.audienceId;
      const isSellerAllocationInvalid = automaticAction.sellerAllocation === null;
      const isMessagesEmpty = !actionsMessages.length;
      const isLimitRequiredAndEmpty =
        automaticAction.limitType === 'nonofficial-automatic' && automaticAction.limitValue === null;

      return (
        isNameEmpty ||
        isCategoryEmpty ||
        isAudienceEmpty ||
        isSellerAllocationInvalid ||
        isMessagesEmpty ||
        isLimitRequiredAndEmpty
      );
    },
    hasEmptyRequiredFieldsSellerAction: state => {
      const { suggestionDetail, automaticAction } = state;

      const isNameEmpty = !suggestionDetail?.name;
      const isCategoryEmpty = !suggestionDetail?.actionCategory;
      const isStoresEmpty = automaticAction.sendChannel !== 1 && !suggestionDetail?.stores;

      return isNameEmpty || isCategoryEmpty || isStoresEmpty;
    },
    getActionTemplates: state => state.actionTemplates,
  },
};

export default suggestions;
